import React from "react";
import { Tooltip, Typography } from "@mui/material";

import {
  fetchFirstRefusalSummary,
  type FirstRefusalSummary,
} from "../../services/leadOpportunityService";

/**
 * PKT-0355-B · The monthly 10%-path accountability line.
 *
 * One sentence under the New Leads feed: how many HIGH-band leads converted to
 * the agent (10%) path this month, with the renter-stated rent total in a
 * tooltip — labeled as stated rent, never as revenue (backend caveats travel
 * with the payload; this component must not present the figure harder than
 * the endpoint does).
 *
 * Self-fetching so the 2,100-line dashboard gains exactly one mounted line,
 * and a fetch failure renders NOTHING rather than a fabricated zero — absence
 * of the line is "unknown", the same honesty rule as everywhere else.
 */
const TenPctPathLine: React.FC = () => {
  const [summary, setSummary] = React.useState<FirstRefusalSummary | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchFirstRefusalSummary()
      .then((value) => {
        if (!cancelled) setSummary(value);
      })
      .catch(() => {
        /* render nothing — never a fake zero */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!summary) return null;
  const count = summary.high_to_agent_path.count;
  const monthLabel = new Date(`${summary.month}-15T00:00:00Z`).toLocaleString(
    "en-US",
    { month: "long", timeZone: "UTC" }
  );
  const rentSum = summary.high_to_agent_path.stated_monthly_rent_sum;
  const detail =
    count === 0
      ? `${monthLabel}: every converted top lead stayed on the full-commission path.`
      : `${monthLabel}: ${count} top ${count === 1 ? "lead" : "leads"} went to the 10% path.`;
  return (
    <Tooltip
      title={
        count > 0 && rentSum > 0
          ? `Stated monthly rent on those leads: $${rentSum.toLocaleString("en-US")} (renter-stated, not revenue)`
          : "Top lead = high revenue-opportunity band at the time the lead arrived"
      }
    >
      <Typography
        variant="caption"
        role="note"
        sx={{
          display: "block",
          mt: -2,
          mb: 3,
          px: 0.5,
          color: count > 0 ? "warning.main" : "text.secondary",
          fontWeight: count > 0 ? 700 : 400,
        }}
      >
        {detail}
      </Typography>
    </Tooltip>
  );
};

export default TenPctPathLine;
